import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InfoCircleFill, ExclamationCircleFill, Copy } from 'react-bootstrap-icons'
import { ColDef, IRowNode, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { LogMessage } from './MessagesOffCanvas';
import { toast } from 'react-toastify';
import { Button } from 'react-bootstrap';
import { dateComparator } from './variables';
import { AG_GRID_LOCALE_RU, BlickAgGrid } from 'blick.react.aggrid';
import { useIsMobile } from './shared';
ModuleRegistry.registerModules([AllCommunityModule]);
interface IMessagesTableProps {
    data: LogMessage[];
    containerRef: RefObject<HTMLDivElement | null>
}
const MessagesTable = (props: IMessagesTableProps) => {
    const { data, containerRef } = props;
    const isMobile = useIsMobile();

    const [selectedLogId, setSelectedLogId] = useState<number | null>(null);

    const getStoredColumnWidth = (column) => {
        const widthsStorage = getWidthStorage();
        if (widthsStorage) {
            const columnWidth = widthsStorage[column];
            if (columnWidth)
                return columnWidth;
        }
        return 150;
    }
    const resizeHandle = (state) => {
        if (!state.column)
            return;

        const colId = state.column.colId;
        const colWidth = state.column.actualWidth;
        let widthsStorage = getWidthStorage();
        if (!widthsStorage)
            widthsStorage = {};

        widthsStorage[colId] = colWidth;
        localStorage.setItem("logMessagesTableWidths", JSON.stringify(widthsStorage));
    }
    function getWidthStorage() {
        return JSON.parse(localStorage.getItem("logMessagesTableWidths") ?? "null");
    }
    const getLogLevelSvg = (level) => {
        switch (level) {
            case 0:
            case 1:
            case 2:
                return <InfoCircleFill className="svg-info" />
            case 3:
                return <ExclamationCircleFill className="svg-warn" />
            case 4:
                return <ExclamationCircleFill className="svg-error" />
            default:
                return "//"
        }
    }
    async function copyTextToClipboard(text) {
        if ('clipboard' in navigator) {
            return await navigator.clipboard.writeText(text);
        } else {
            // Fallback for older browsers (less reliable)
            return document.execCommand('copy', true, text);
        }
    }
    const handleCopyClick = (copyText: string) => {
        //TODO - вытащить в blick.react.utils
        copyTextToClipboard(copyText)
            .then(() => {
                toast.success("Скопировано в буфер обмена", { autoClose:700 })
            })
            .catch((err) => {
                console.error('Не удалось скопировать в буфер обмена: ', err);
            });
    };
    const columns = useMemo<ColDef<LogMessage>[]>(
        () => [
            {
                field: "ServiceName",
                headerName: "Источник",
                editable: false,
                maxWidth: 200,
                suppressSizeToFit: true,
                width: getStoredColumnWidth("source"),
                autoHeight: true,
                wrapText: true,
                cellRenderer: (row: CustomCellRendererProps<LogMessage>) => {
                    const data = row.data;

                    const level = data?.EntryType;
                    return (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label>{data?.ServiceName}</label>
                            {getLogLevelSvg(level) }
                        </div>
                    );
                },
            },
            {
                field: "Time",
                headerName: "Время",
                editable: false,
                sort: 'asc',
                comparator: dateComparator,
                equals: (message1, message2) => {
                    return message1 == message2;
                },
                suppressSizeToFit: true,
                cellRenderer: (row: CustomCellRendererProps<LogMessage>) => {
                    const value = row.value;
                    if (value == undefined)
                        return;

                    const formatedDate = value.toLocaleString(
                        "ru-RU")
                    return formatedDate;
                },
                valueGetter: (row) => {
                    const value = row.data![row.colDef.field!];
                    if (value == undefined)
                        return;

                    const formatedDate = new Date(value.Seconds * 1000);
                    return formatedDate;
                },
            },
            {
                field: "Message",
                headerName: "Сообщение",
                editable: false,
                cellRenderer: (node: CustomCellRendererProps<LogMessage>) =>
                    <div title={node.data!.Message}>
                        <p></p>{node.data!.Message}
                        <Button style={{ position: 'absolute', bottom: '0', right:'0', padding: '0 .25rem' }} variant='transparent' className='copyClipboard' onClick={() => handleCopyClick(node.data!.Message)}>
                            <Copy/>
                        </Button>
                    </div>
            }
        ],
        []
    )
    const rowClickHandler = (event: RowClickedEvent) => {
        try {
            const rowData: LogMessage = event.node.data;
            if (selectedLogId == rowData.LogId) {
                event.node.setSelected(false);
                setSelectedLogId(null);
            }
            else {
                event.node.setSelected(true);
                setSelectedLogId(rowData.LogId);
            }
        }
        catch (e) { console.log(e) }
    }

    useEffect(() => {
        if (!tableRef.current?.api)
            return;

        const node = findRowByLogId(selectedLogId);
        if (!node) {
            scrollToLastRow();
            //tableRef.current?.api.ensureIndexVisible(data.length - 1, "middle");
        }
        else if(node.id) {
            const selected = tableRef.current?.api?.getRowNode(node.id);
            if (selected) {
                setTimeout(function () {
                    tableRef.current?.api.ensureIndexVisible(selected.rowIndex!, "middle");
                    tableRef.current?.api.setNodesSelected({ nodes: [selected], newValue: true });
                }, 0)
            }
        }
    }, [data]);

    const findRowByLogId = (logId): IRowNode<LogMessage> | null => {
        let foundNode: IRowNode<LogMessage> | null = null;
        tableRef.current?.api.forEachNode((node) => {
            const rowData: LogMessage = node.data;
            if (rowData.LogId == logId)
                foundNode = node;
        })
        return foundNode;
    }
    const tableRef = useRef<AgGridReact>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const scrollToLastCard = () => {
        setTimeout(() => {
            const el = listRef.current;
            if (!el)
                return;
            el.scrollTop = el.scrollHeight;
            setTimeout(() => {
                el.scrollTop = el.scrollHeight;
            }, 200)
        }, 200)
    }
    const scrollCardIntoCenter = (logId: number) => {
        setTimeout(() => {
            const el = listRef.current;
            const card = cardRefs.current.get(logId);
            if (!el || !card)
                return;
            el.scrollTop = card.offsetTop - el.clientHeight / 2 + card.clientHeight / 2;
        }, 0)
    }
    useEffect(() => {
        if (!isMobile)
            return;
        if (selectedLogId == null)
            scrollToLastCard();
        else
            scrollCardIntoCenter(selectedLogId);
    }, [data]);
    const scrollToLastRow = () => {
        if (data.length === 0)
            return;
        setTimeout(() => {
            tableRef.current?.api.ensureIndexVisible(0, "top");
            setTimeout(() => {
                tableRef.current?.api.ensureIndexVisible(0, "top")
            }, 200)
        }, 200);
        sizeToFit();
    }
    const sizeToFit = useCallback(() => {
        // AG Grid #29: не вызываем sizeColumnsToFit, если контейнер ещё не виден (нулевая ширина)
        const el = containerRef?.current;
        if (!el || el.clientWidth === 0)
            return;
        setTimeout(() => {
            tableRef.current?.api?.sizeColumnsToFit({
                defaultMinWidth: 50,
            });
        }, 0)
    }, [containerRef]);
    useEffect(() => {
        if (!containerRef?.current) return;

        const observer = new ResizeObserver(() => {
            sizeToFit();
        })
        observer.observe(containerRef?.current);
        return () => {
            observer.disconnect(); // Clean up observer on unmount
        };
    }, [containerRef])
    return (
        <>
            {isMobile ?
                <div ref={listRef} className="mobileMessagesList">
                    {data.length === 0 ?
                        <div className="mobileMessagesEmpty">Нет сообщений</div>
                        :
                        data.map((message) => {
                            const time = message.Time ? new Date(message.Time.Seconds * 1000).toLocaleString("ru-RU") : "";
                            return (
                                <div key={message.LogId} ref={(el) => { if (el) cardRefs.current.set(message.LogId, el); else cardRefs.current.delete(message.LogId); }}
                                    className={'mobileMessagesCard' + (selectedLogId == message.LogId ? ' selected' : '')}
                                    onClick={() => selectedLogId == message.LogId ? setSelectedLogId(null) : setSelectedLogId(message.LogId)}>
                                    <div className="mobileMessagesCardHeader">
                                        <span className="mobileMessagesCardLevel">
                                            {getLogLevelSvg(message.EntryType)}
                                        </span>
                                        <span className="mobileMessagesCardSource">{message.ServiceName}</span>
                                        <span className="mobileMessagesCardTime">{time}</span>
                                    </div>
                                    <div className="mobileMessagesCardText">{message.Message}</div>
                                    <Button variant="transparent" className="mobileMessagesCardCopy"
                                        title="Скопировать" onClick={(e) => { e.stopPropagation(); handleCopyClick(message.Message); }}>
                                        <Copy />
                                    </Button>
                                </div>
                            );
                        })
                    }
                </div>
                :
                <BlickAgGrid
                    showRestoreColumnsButton={true}
                    rowData={data}
                    ref={tableRef}
                    //theme={myAgReactTheme}
                    localeText={AG_GRID_LOCALE_RU}
                    columnDefs={columns}
                    animateRows={false}
                    rowSelection={{ mode: "singleRow", checkboxes: false }}
                    suppressScrollOnNewData={true}
                    rowHeight={48 }
                    onColumnResized={resizeHandle}
                    onRowClicked={rowClickHandler}
                    onSortChanged={(e) => { e.api.refreshCells() }}
                    suppressHorizontalScroll={true}
                    onGridReady={scrollToLastRow}
                />
            }
        </>
    )
;
};

export default MessagesTable;
